import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, switchMap } from 'rxjs';
import { LucideAngularModule, User, ArrowRight } from 'lucide-angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { AuthService } from '../auth.service';
import { UserType } from '../auth.models';
import { CompleteProfileConfig, CompleteProfileService } from './complete-profile.service';
import { PhoneInputComponent } from '../../components/phone-input/phone-input';

type ProfileType = 'teacher' | 'student';

@Component({
  selector: 'app-complete-profile-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    TranslatePipe,
    PhoneInputComponent,
  ],
  templateUrl: './complete-profile-modal.html',
  styleUrl: './complete-profile-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompleteProfileModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  protected readonly completeProfileService = inject(CompleteProfileService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly errorMessage$ = new BehaviorSubject<string>('');
  protected readonly profileType = signal<ProfileType>('teacher');

  protected profileForm!: FormGroup;
  private config: CompleteProfileConfig | null = null;

  protected readonly icons = { User, ArrowRight };

  ngOnInit(): void {
    this.completeProfileService.config$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((config) => {
        this.config = config;
        if (config) {
          const type = (config.userType as UserType) === 'student' ? 'student' : 'teacher';
          this.profileType.set(type);
          this.buildForm();
          this.errorMessage$.next('');
          this.isLoading$.next(false);
        }
      });
  }

  private buildForm(): void {
    const controls: Record<string, any> = {
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
    };

    if (this.profileType() === 'student') {
      controls['parentName'] = ['', [Validators.required]];
      controls['parentPhoneNumber'] = ['', [Validators.required]];
    } else {
      controls['phoneNumber'] = ['', [Validators.required]];
    }

    this.profileForm = this.fb.group(controls);
  }

  protected onSubmit(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);
    this.errorMessage$.next('');

    const formValue = this.profileForm.value;

    const request$ =
      this.profileType() === 'student'
        ? this.authService.completeStudentProfile({
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            parentName: formValue.parentName,
            parentPhoneNumber: formValue.parentPhoneNumber,
          })
        : this.authService.completeTeacherProfile({
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            phoneNumber: formValue.phoneNumber,
          });

    request$
      .pipe(
        switchMap(() => this.authService.refreshProfile()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.isLoading$.next(false);
          const redirectUrl = this.config?.redirectUrl;
          this.completeProfileService.close();
          if (redirectUrl) {
            this.router.navigateByUrl(redirectUrl);
          }
        },
        error: (error) => {
          this.isLoading$.next(false);
          this.errorMessage$.next(
            error.error?.message ||
              this.translateService.instant(_('common.auth.completeProfile.genericError')),
          );
        },
      });
  }
}
